import { Divider, FormGroup, FormParagraph } from '@ksyos/design-system';
import { CustomControlMode, ServiceCode } from '@common/enums';
import type { IOrthOProcedureCustomControlData } from '../../../../../common/interfaces/ophthalmology/orthO/orthOProcedureCustomControlData';
import type { ValidationErrors } from '../../../../utils/validation';
import type { ICustomControlProcedureReadProps } from '../interfaces/';
import { getServiceLabel } from '../../../../../common/customControls/getServiceLabel';
import { getDisplayName } from '../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../translate';
import { useEpisodeContext } from '../../../../utils/hooks/useEpisodeContext';
import { ProcedureReadButtons } from '../../../elements/carePathButtons/ProcedureReadButtons';
import { HeaderDivider } from '../../../elements/common';
import { PatientInsuranceOldCarepath } from '../../episodeCategory/customControls/patientInsurance/PatientInsuranceOldCarepath';
import { ProcessContainer } from '../../episodes/common';
import { ProcessItemFooter } from '../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../utils/v1/CustomControlCollapseBody';
import { OrthOV1ProcedureAnamnese } from './OrthOV1ProcedureAnamnese';
import { OrthOV1ProcedureExaminations } from './OrthOV1ProcedureExaminations';
import { OrthOV1ProcedurePhotos } from './OrthOV1ProcedurePhotos';

interface IOrthopticsV1ProcedureReadProps extends ICustomControlProcedureReadProps {
    isOrthOFol?: boolean;
}
export interface IOrthOV1ProcedureProps {
    customControlData: IOrthOProcedureCustomControlData;
    errors?: ValidationErrors;
    handleChangeCustomControl?: <T = IOrthOProcedureCustomControlData>(values: Partial<T>) => Promise<void>;
    isOrthOFol?: boolean;
    isReadOnly?: boolean;
    validateOneCustomControl?: (name: string) => void;
}

export function OrthOV1ProcedureRead(props: IOrthopticsV1ProcedureReadProps) {
    const { patient } = useEpisodeContext();
    const { customControlData }: { customControlData: IOrthOProcedureCustomControlData } = props.procedure;

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process.procedure && props.process.procedure.dateSent}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || !!props.itemState.initialIsOpen,
                    })
                }
                organizationName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerOrganization?.name
                        ? props.process.procedure.performerOrganization.name
                        : undefined
                }
                practitionerName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerPractitioner
                        ? getDisplayName(props.process.procedure.performerPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                showProcessIcon
                title={getServiceLabel(props.process.code, 'procedure', props.process.name)}
            />

            <PatientInsuranceOldCarepath
                isPatientInformed={props.procedure.customControlData.isPatientInformed}
                mode={CustomControlMode.read}
                serviceCode={props.isOrthOFol ? ServiceCode.OrthOFol : ServiceCode.OrthO}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <HeaderDivider text="Huisarts" />
                <FormGroup label="Huisarts">
                    <FormParagraph>
                        {patient.authorization?.canSeePatientDetails && props.process.requesterPractitioner
                            ? getDisplayName(props.process.requesterPractitioner)
                            : undefined}
                    </FormParagraph>
                </FormGroup>

                {!props.isOrthOFol && (customControlData.verwijzer_overig || customControlData.verwijzer) && (
                    <FormGroup label="Verwijzer">
                        <FormParagraph>
                            {customControlData.verwijzer_overig ||
                                translateKey(`orthOVerwijzerEnum.${customControlData.verwijzer}`)}
                        </FormParagraph>
                    </FormGroup>
                )}

                <Divider
                    color="minimal"
                    spacing="medium"
                />
                <OrthOV1ProcedureAnamnese
                    customControlData={customControlData}
                    isReadOnly
                />

                <Divider
                    color="minimal"
                    spacing="medium"
                />
                <OrthOV1ProcedureExaminations
                    customControlData={customControlData}
                    isReadOnly
                />

                <Divider
                    color="minimal"
                    spacing="medium"
                />
                <OrthOV1ProcedurePhotos
                    {...props}
                    isOrthOFol={props.isOrthOFol}
                    isReadOnly
                    title="Foto's"
                />
            </CustomControlCollapseBody>
            <ProcessItemFooter
                isItemOpen={props.itemState.isOpen}
                labelLess={translateKey('procedure.hideProcedure')}
                labelMore={translateKey('procedure.showProcedure')}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
            />
            <ProcedureReadButtons {...props} />
        </ProcessContainer>
    );
}
